/*
 * SIXSENSE automatic free gift.
 *
 * When the cart holds `threshold` or more standalone Couples Chocolate boxes,
 * exactly one Teases Couples Card Game is added and tagged with a private line
 * item property, so it can be taken back out again when the cart stops
 * qualifying. Shopify's automatic Buy X Get Y discount is what actually makes
 * the gift free — this module only ever changes cart contents, never prices.
 *
 * Configuration comes from `window.sixsenseFreeGift`, emitted by theme.liquid.
 * Cart mutations are driven from the cart drawer lifecycle (see cart-drawer.js);
 * the cart page has no AJAX cart, so it syncs once on load instead.
 */

(function () {
  var GIFT_PROPERTY = '_sixsense_free_gift';
  var DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  var RELOAD_FLAG = 'sixsenseFreeGiftReloaded';
  var MAX_FOLLOW_UP_PASSES = 5;

  var running = false;
  var queued = false;
  var current = Promise.resolve();

  function config() {
    return window.sixsenseFreeGift;
  }

  // Deliberately does not require the gift to be *available*: one already in
  // the cart must still be removable after it sells out, and the quantity
  // controls on that row are hidden.
  function isEnabled() {
    var settings = config();
    return Boolean(settings && settings.giftProductId && settings.giftVariantId && settings.qualifyingProductId);
  }

  function isCartPage() {
    return Boolean(document.getElementById('main-cart'));
  }

  function hasDrawer() {
    return Boolean(document.querySelector('cart-drawer'));
  }

  /* ---------------------------------------------------------------- state -- */

  function fetchCart() {
    return fetch('/cart.js?r=' + Date.now(), { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Free gift: could not read the cart');
        return response.json();
      });
  }

  // The gift product, whoever put it in the cart.
  function isGiftLine(item) {
    return item.product_id === config().giftProductId;
  }

  function isAutoGiftLine(item) {
    return isGiftLine(item) && Boolean(item.properties) && String(item.properties[GIFT_PROPERTY]) === 'true';
  }

  // Only the standalone chocolate product counts. Bundles that happen to
  // contain chocolate deliberately do not qualify.
  function qualifyingQuantity(cart) {
    var qualifyingProductId = config().qualifyingProductId;
    return cart.items.reduce(function (total, item) {
      return item.product_id === qualifyingProductId ? total + item.quantity : total;
    }, 0);
  }

  // Derives the single mutation needed to reconcile the cart, or none at all.
  function plan(cart) {
    var qualifies = qualifyingQuantity(cart) >= config().threshold;
    var giftLines = cart.items.filter(isGiftLine);
    var autoLine = giftLines.filter(isAutoGiftLine)[0];

    if (!qualifies) {
      return autoLine ? { action: 'change', key: autoLine.key, quantity: 0 } : { action: 'none' };
    }

    if (autoLine) {
      return autoLine.quantity === 1 ? { action: 'none' } : { action: 'change', key: autoLine.key, quantity: 1 };
    }

    // A Teases the customer added themselves already claims the discount;
    // adding a second one would leave them paying full price for it.
    if (giftLines.length) return { action: 'none' };

    return config().giftAvailable ? { action: 'add' } : { action: 'none' };
  }

  /* ------------------------------------------------------------ mutations -- */

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Free gift: cart update rejected');
        }
        return data;
      });
    });
  }

  // Sections are only worth requesting where the drawer can render them.
  function withSections(body) {
    if (hasDrawer()) body.sections = DRAWER_SECTIONS;
    return body;
  }

  function addGift() {
    var properties = {};
    properties[GIFT_PROPERTY] = 'true';

    return postJSON(window.routes.cart_add_url, withSections({
      items: [{ id: config().giftVariantId, quantity: 1, properties: properties }]
    }));
  }

  // Always addressed by line item key, never by line index: our own add and
  // remove shift the indices the rest of the theme works with.
  function changeGift(key, quantity) {
    return postJSON(window.routes.cart_change_url, withSections({ id: key, quantity: quantity }));
  }

  /* --------------------------------------------------------------- render -- */

  function replaceContents(source, selector) {
    var incoming = source.querySelector(selector);
    var target = document.querySelector(selector);
    if (incoming && target) target.innerHTML = incoming.innerHTML;
  }

  function updateFreeShipping(source) {
    var incoming = source.querySelector('.cart__free-shipping');
    var target = document.querySelector('.cart__free-shipping');
    if (!incoming || !target || typeof target.updateShippingIndicator !== 'function') return;
    target.totalPrice = incoming.getAttribute('total-price');
    target.updateShippingIndicator();
  }

  function renderSections(sections) {
    if (!sections) return;

    if (sections['cart-drawer']) {
      var drawer = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      replaceContents(drawer, '.drawer__heading');
      replaceContents(drawer, '.drawer__contents');
      updateFreeShipping(drawer);
    }

    if (sections['cart-icon-bubble']) {
      var bubble = new DOMParser()
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('.shopify-section');

      if (bubble) {
        document.querySelectorAll('#cart-icon-bubble').forEach(function (target) {
          (target.querySelector('.shopify-section') || target).innerHTML = bubble.innerHTML;
        });
      }
    }

    // Melting protection owns the drawer subtotal; let it recalculate.
    if (typeof updateSubtotals === 'function') updateSubtotals();
  }

  /* ------------------------------------------------------ cart page reload -- */

  // The cart page has no AJAX cart, so a mutation has to be re-rendered by the
  // server. The flag makes a reload loop impossible if the cart never settles.
  function reloadCartPage() {
    try {
      if (sessionStorage.getItem(RELOAD_FLAG) === 'true') return;
      sessionStorage.setItem(RELOAD_FLAG, 'true');
    } catch (error) {
      return;
    }
    window.location.reload();
  }

  function clearReloadFlag() {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch (error) {
      /* sessionStorage unavailable — nothing to clear */
    }
  }

  /* ----------------------------------------------------------------- sync -- */

  function runSync() {
    if (!isEnabled()) return Promise.resolve();

    return fetchCart()
      .then(function (cart) {
        var step = plan(cart);
        if (step.action === 'none') {
          clearReloadFlag();
          return null;
        }
        return step.action === 'add' ? addGift() : changeGift(step.key, step.quantity);
      })
      .then(function (result) {
        if (!result) return;
        renderSections(result.sections);
        if (isCartPage()) reloadCartPage();
      })
      .catch(function (error) {
        // Gift handling must never break the cart or block checkout.
        console.error('Free gift sync failed', error);
      });
  }

  // Passes are serialised, and a request arriving mid-pass is coalesced into a
  // single follow-up rather than running concurrently, so rapid quantity
  // changes converge instead of racing.
  function sync() {
    if (running) {
      queued = true;
      return current;
    }

    running = true;
    current = (async function () {
      try {
        await runSync();

        var passes = 0;
        while (queued && passes < MAX_FOLLOW_UP_PASSES) {
          queued = false;
          passes += 1;
          await runSync();
        }
        queued = false;
      } finally {
        running = false;
      }
    })();

    return current;
  }

  window.SixsenseFreeGift = { sync: sync };

  function init() {
    // Drawer pages sync through the CartDrawer lifecycle. Pages without a
    // drawer (the cart page) have no AJAX cart, so sync once on load.
    if (!hasDrawer()) sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
